import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { apiService } from "../services/api";
import type { CompanyBase } from "../types/company";
import type { ISlider } from "../types/slider";
import { useLanguage } from "../contexts/LanguageContext";
import { useFavourites } from "@/contexts/FavouritesProvider";

interface SliderProps {
  selectedCompany: CompanyBase;
}

export function Slider({ selectedCompany }: SliderProps) {
  const [slides, setSlides] = useState<ISlider[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    if (selectedCompany) {
      const fetchSliders = async () => {
        setIsLoading(true);
        try {
          const slides = await apiService.getSliders(selectedCompany.id);
          setSlides(slides);
        } catch (error) {
          setSlides([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchSliders();
    } else {
      setSlides([]);
      setIsLoading(false);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (slides.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const { isFavouriteCompany, addFavouriteCompany, removeFavouriteCompany } =
    useFavourites();

  if (isLoading) {
    return (
      <div className="relative h-64 md:h-96 lg:h-[500px] bg-gray-200 animate-pulse">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-primary-light border-t-primary rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="relative h-64 md:h-96 lg:h-[500px] bg-gray-200 flex items-center justify-center">
        <div className="text-center text-black">
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            {t("home.welcome")} {selectedCompany?.name || "Our Restaurant"}
          </h2>
          <p className="text-lg md:text-xl opacity-90">
            {t("home.fresh_ingredients")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-64 md:h-96 lg:h-[500px] overflow-hidden">
      <div className="absolute inset-0">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentSlide ? "opacity-100" : "opacity-0"
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title || `Slide ${index + 1}`}
              className="w-full h-full object-cover z-0"
            />
            <div
              className="z-20 absolute right-4 bottom-4 px-4 py-2 bg-rose-50 ring-1 ring-rose-500 rounded-lg flex justify-center items-center gap-2 hover:bg-rose-100 hover:text-rose-600 transition-all duration-300 cursor-pointer"
              onClick={() => {
                console.log(selectedCompany);

                if (isFavouriteCompany(selectedCompany.id)) {
                  removeFavouriteCompany(selectedCompany);
                } else {
                  addFavouriteCompany({
                    id: selectedCompany.id,
                    name: selectedCompany.name,
                    address: selectedCompany.settings.address,
                    workingHours: selectedCompany.settings.working_hours,
                    image: selectedCompany.settings.logo,
                  });
                }
              }}
            >
              <Heart
                className={`w-6 h-6 text-rose-500 ${
                  isFavouriteCompany(selectedCompany.id)
                    ? "fill-rose-500"
                    : "fill-none"
                }`}
              />
              <span className="text-sm text-rose-500">
                {isFavouriteCompany(selectedCompany.id)
                  ? "Remove from favourites"
                  : "Add to favourites"}
              </span>
            </div>
            <div className="absolute inset-0 bg-black/30 z-10"></div>

            {slide.title && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-white">
                  <h2 className="text-3xl md:text-5xl font-bold mb-4">
                    {slide.title}
                  </h2>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute start-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-all duration-200"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute end-4 top-1/2 -translate-y-1/2 p-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-full transition-all duration-200"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
          <div className="absolute bottom-4 start-1/2 -translate-x-1/2 flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentSlide ? "bg-white" : "bg-white bg-opacity-50"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
